import React, { useState, useMemo } from 'react';
import { CheckSquare, Square, Eye, Edit, Trash2, Star, MapPin, Home, Video, Calendar, MessageSquare, Bed, Bath, Square as SquareIcon, Package } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const PropertyTable = ({ 
  properties, 
  loading, 
  selectedProperties, 
  setSelectedProperties, 
  onPropertyAction 
}) => {
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount || 0);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'dd/MM/yyyy', { locale: fr });
    } catch {
      return 'Date invalide';
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      available: { color: 'bg-green-100 text-green-800', text: 'Disponible' },
      reserved: { color: 'bg-yellow-100 text-yellow-800', text: 'Réservé' },
      sold: { color: 'bg-blue-100 text-blue-800', text: 'Vendu' },
      withdrawn: { color: 'bg-red-100 text-red-800', text: 'Retiré' }
    };

    const config = statusConfig?.[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.text}
      </span>
    );
  };

  const getPropertyTypeBadge = (type) => {
    const typeConfig = {
      apartment: { color: 'bg-blue-100 text-blue-800', text: 'Appartement' },
      house: { color: 'bg-green-100 text-green-800', text: 'Maison' },
      land: { color: 'bg-yellow-100 text-yellow-800', text: 'Terrain' },
      commercial: { color: 'bg-purple-100 text-purple-800', text: 'Commercial' },
      villa: { color: 'bg-indigo-100 text-indigo-800', text: 'Villa' }
    };

    const config = typeConfig?.[type] || { color: 'bg-gray-100 text-gray-800', text: type };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        {config?.text}
      </span>
    );
  };

  const handleSelectAll = () => {
    if (selectedProperties?.length === properties?.length) {
      setSelectedProperties([]);
    } else {
      setSelectedProperties(properties?.map(p => p?.id) || []);
    }
  };

  const handleSelectProperty = (propertyId) => {
    if (selectedProperties?.includes(propertyId)) {
      setSelectedProperties(selectedProperties?.filter(id => id !== propertyId));
    } else {
      setSelectedProperties([...selectedProperties, propertyId]);
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedProperties = React.useMemo(() => {
    if (!properties?.length) return [];
    
    return [...properties]?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      // Handle nested values
      if (sortConfig?.key === 'commune') {
        aValue = a?.commune?.name || '';
        bValue = b?.commune?.name || '';
      } else if (sortConfig?.key === 'agent') {
        aValue = a?.agent?.full_name || '';
        bValue = b?.agent?.full_name || '';
      } else if (sortConfig?.key === 'owner') {
        aValue = a?.owner?.full_name || '';
        bValue = b?.owner?.full_name || '';
      }

      if (aValue < bValue) {
        return sortConfig?.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig?.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [properties, sortConfig]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-2 text-gray-600">Chargement des propriétés...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">
          Toutes les Propriétés ({properties?.length || 0} propriétés)
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Gestion complète de toutes les propriétés du système
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left">
                <button
                  onClick={handleSelectAll}
                  className="flex items-center text-gray-400 hover:text-gray-600"
                >
                  {selectedProperties?.length === properties?.length ? (
                    <CheckSquare className="h-4 w-4" />
                  ) : (
                    <Square className="h-4 w-4" />
                  )}
                </button>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                onClick={() => handleSort('title')}
              >
                Propriété & Détails
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                onClick={() => handleSort('property_type')}
              >
                Type & Caractéristiques
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                onClick={() => handleSort('commune')}
              >
                Localisation
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                onClick={() => handleSort('price')}
              >
                Prix & Surface
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                onClick={() => handleSort('status')}
              >
                Statut
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Médias & Documents
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                onClick={() => handleSort('agent')}
              >
                Gestion
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700"
                onClick={() => handleSort('created_at')}
              >
                Dates
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedProperties?.map((property) => (
              <tr key={property?.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleSelectProperty(property?.id)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    {selectedProperties?.includes(property?.id) ? (
                      <CheckSquare className="h-4 w-4" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-12 w-12">
                      {property?.images?.[0] ? (
                        <img
                          className="h-12 w-12 rounded object-cover border border-gray-200"
                          src={property?.images?.[0]}
                          alt={property?.title}
                        />
                      ) : (
                        <div className="h-12 w-12 rounded bg-gray-200 flex items-center justify-center border">
                          <Home className="h-6 w-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-semibold text-gray-900">
                        {property?.title}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center mt-1">
                        <MapPin className="h-3 w-3 mr-1" />
                        {property?.address}
                      </div>
                      {property?.description && (
                        <div className="text-xs text-gray-400 mt-1 max-w-xs truncate">
                          {property?.description}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    {getPropertyTypeBadge(property?.property_type)}
                    <div className="flex items-center space-x-3 text-sm text-gray-600">
                      {property?.bedrooms && (
                        <div className="flex items-center">
                          <Bed className="h-3 w-3 mr-1" />
                          {property?.bedrooms}
                        </div>
                      )}
                      {property?.bathrooms && (
                        <div className="flex items-center">
                          <Bath className="h-3 w-3 mr-1" />
                          {property?.bathrooms}
                        </div>
                      )}
                    </div>
                    {property?.features && Object.keys(property?.features)?.length > 0 && (
                      <div className="text-xs text-gray-500">
                        {Object.entries(property?.features)?.filter(([key, value]) => value === true)?.map(([key]) => key)?.slice(0, 3)?.join(', ')}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900 font-medium">
                    {property?.commune?.name || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {property?.commune?.district || ''}
                  </div>
                  {property?.coordinates && (
                    <div className="text-xs text-gray-400 mt-1">
                      {property?.coordinates?.lat?.toFixed(4)}, {property?.coordinates?.lng?.toFixed(4)}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-bold text-gray-900">
                    {formatCurrency(property?.price)}
                  </div>
                  {property?.surface_area && (
                    <div className="text-sm text-gray-500 flex items-center mt-1">
                      <SquareIcon className="h-3 w-3 mr-1" />
                      {property?.surface_area} m²
                    </div>
                  )}
                  {property?.surface_area && property?.price && (
                    <div className="text-xs text-gray-400">
                      {formatCurrency(property?.price / property?.surface_area)}/m²
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(property?.status)}
                  {property?.features?.featured && (
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                        <Star className="h-3 w-3 mr-1" />
                        Vedette
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center text-sm text-gray-500">
                        <Home className="h-4 w-4 mr-1" />
                        {property?.images?.length || 0} photos
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center text-sm text-gray-500">
                        <Video className="h-4 w-4 mr-1" />
                        {property?.videos?.length || 0} vidéos
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    <div className="text-sm text-gray-900">
                      Agent: {property?.agent?.full_name || 'Non assigné'}
                    </div>
                    <div className="text-xs text-gray-500">
                      {property?.agent?.email || ''}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Propriétaire: {property?.owner?.full_name || 'Non défini'}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="space-y-1">
                    <div className="text-sm text-gray-900">
                      Créé: {formatDate(property?.created_at)}
                    </div>
                    {property?.updated_at !== property?.created_at && (
                      <div className="text-xs text-gray-500 mt-1">
                        MAJ: {formatDate(property?.updated_at)}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex items-center space-x-2">
                    <button 
                      className="text-blue-600 hover:text-blue-900"
                      title="Voir les détails"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button 
                      className="text-green-600 hover:text-green-900"
                      onClick={() => onPropertyAction?.(property?.id, 'edit')}
                      title="Modifier"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button 
                      className="text-purple-600 hover:text-purple-900"
                      onClick={() => onPropertyAction?.(property?.id, 'videos')}
                      title="Gérer les vidéos"
                    >
                      <Video className="h-4 w-4" />
                    </button>
                    <button
                      className="text-indigo-500 hover:text-indigo-800"
                      onClick={() => onPropertyAction?.(property?.id, 'obj-import')}
                      title="Importer modèles .obj 3D"
                    >
                      <Package className="h-4 w-4" />
                    </button>
                    <button 
                      className="text-indigo-600 hover:text-indigo-900"
                      onClick={() => onPropertyAction?.(property?.id, 'appointments')}
                      title="Rendez-vous"
                    >
                      <Calendar className="h-4 w-4" />
                    </button>
                    <button 
                      className="text-orange-600 hover:text-orange-900"
                      onClick={() => onPropertyAction?.(property?.id, 'inquiries')}
                      title="Demandes"
                    >
                      <MessageSquare className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onPropertyAction?.(property?.id, 'feature')}
                      className={`${property?.features?.featured ? 'text-yellow-600' : 'text-gray-400'} hover:text-yellow-900`}
                      title="Mettre en vedette"
                    >
                      <Star className="h-4 w-4" />
                    </button>
                    <button 
                      className="text-red-600 hover:text-red-900"
                      onClick={() => onPropertyAction?.(property?.id, 'delete')}
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {sortedProperties?.length === 0 && (
          <div className="p-12 text-center">
            <Home className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune propriété trouvée</h3>
            <p className="text-gray-600 mb-6">
              Il n'y a actuellement aucune propriété dans le système ou toutes les propriétés sont filtrées.
            </p>
            <button 
              onClick={() => onPropertyAction?.(null, 'refresh')}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              Actualiser la liste
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertyTable;